/**
 * Federal paycheck / tax readiness estimate — tax-year constants (centralized).
 * Update amounts here each year; brackets align with IRS inflation adjustments.
 *
 * Sources (tax year 2026):
 * - Social Security wage base: SSA / IRS published OASDI contribution and benefit base
 * - Employee FICA rates: IRC §§3101, 3111 (6.2% OASDI, 1.45% Medicare)
 * - Additional Medicare Tax: IRC §3101(b)(2); Form 8959 thresholds
 * - Self-employment tax: IRC §1401; Schedule SE (92.35% net-earnings factor)
 * - Ordinary brackets & standard deduction: IRS inflation-adjusted schedules
 */

export const FEDERAL_PAYCHECK_TAX_CONFIG = {
  taxYear: 2026,

  /**
   * Additional Medicare Tax thresholds on combined Medicare wages + SE income
   * (Form 8959 / IRC §3101(b)(2)).
   */
  additionalMedicareWageThresholds: {
    single: 200_000,
    head_of_household: 200_000,
    married_filing_jointly: 250_000,
    married_filing_separately: 125_000,
    qualifying_surviving_spouse: 200_000,
  },

  /** Employer begins withholding Additional Medicare Tax once wages to that employee exceed this. */
  additionalMedicareEmployerWithholdingThreshold: 200_000,

  /**
   * Net Investment Income Tax (NIIT) MAGI thresholds — same dollar amounts as Additional Medicare
   * for Single / MFJ (IRC §1411). Shown on the tax ladder as a planning marker only.
   */
  niitMagiThresholds: {
    single: 200_000,
    head_of_household: 200_000,
    married_filing_jointly: 250_000,
    married_filing_separately: 125_000,
    qualifying_surviving_spouse: 200_000,
  },

  niitRate: 0.038,

  socialSecurity: {
    employeeRate: 0.062,
    selfEmploymentRate: 0.124,
    /** 2026 OASDI wage base (per person — never combined household). */
    annualWageBase: 184_500,
  },

  medicare: {
    employeeRate: 0.0145,
    selfEmploymentRate: 0.029,
    additionalRate: 0.009,
  },

  selfEmployment: {
    /** Schedule SE: net profit × this factor = net earnings subject to SE tax. */
    netEarningsFactor: 0.9235,
    /** Minimum net earnings from self-employment to owe SE tax. */
    filingThreshold: 400,
    /** Deductible portion of SE tax for AGI (generally one-half). */
    deductiblePortionRate: 0.5,
  },

  standardDeduction: {
    single: 16_100,
    married_filing_jointly: 32_200,
    head_of_household: 24_150,
    married_filing_separately: 16_100,
    qualifying_surviving_spouse: 32_200,
  },

  /**
   * Ordinary income — upper bound of each bracket (inclusive bands in estimator).
   * Last entry uses Infinity.
   */
  federalIncomeBrackets: {
    single: [
      { upTo: 12_400, rate: 0.1 },
      { upTo: 50_400, rate: 0.12 },
      { upTo: 105_700, rate: 0.22 },
      { upTo: 201_775, rate: 0.24 },
      { upTo: 256_225, rate: 0.32 },
      { upTo: 640_600, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    married_filing_jointly: [
      { upTo: 24_800, rate: 0.1 },
      { upTo: 100_800, rate: 0.12 },
      { upTo: 211_400, rate: 0.22 },
      { upTo: 403_550, rate: 0.24 },
      { upTo: 512_450, rate: 0.32 },
      { upTo: 768_700, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
  },
};

export const INCOME_TYPES = {
  W2: 'w2',
  SELF_EMPLOYED: 'self_employed',
  MIXED: 'mixed',
};
